// First-run demonstration of the mobile info stack's lift gesture, plus the
// clearance guard for the grabber that labels it.
//
// On mobile the info panel (.shabibi-pdp-col--info) rests almost entirely
// below the fold — only a ~48px strip of the info card shows above the pinned
// Add to Cart button. Everything that decides a purchase (colour, size, the
// size guide) is in the part you have to pull up, and nothing on the page
// below the CTA hints that it moves.
//
// The grabber (see .shabibi-card-grabber in the stylesheet) symbolises the
// gesture. This file demonstrates it: once per session the stack lifts partway
// and settles back. The two are not alternatives — under reduced motion this
// never runs and the grabber is the only thing left doing the job, which is
// why neither ships alone.
//
// The panel is a plain scroll container, so the gesture being advertised is
// native scrolling. That is genuinely one-to-one direct manipulation on touch
// (press the card, drag, it tracks the finger), so the grabber is not a false
// promise and no custom drag implementation is needed — adding one would only
// fight native momentum and iOS rubber-banding.

(() => {
// Byte-identical to the query in shabibi-pdp-media.js, the mobile block in
// section-shabibi-product-main.css, and the inline layout script in
// sections/shabibi-product-main.liquid. Plain max-width can't tell a 12.9"
// iPad Pro in portrait from a smaller iPad in landscape — same width,
// different layout — so all four must move together.
const MOBILE_QUERY = '(max-width: 767px), (max-width: 1024px) and (orientation: portrait)';

// sessionStorage, not localStorage, and one key for the whole store rather
// than one per product: moving between PDPs in a single visit should not
// replay the demo, but a visitor returning a week later has forgotten the
// gesture and should see it again.
const SESSION_KEY = 'shabibi:pdp-lift-demo';

const DEMO_DELAY = 1200;
const DEMO_TRAVEL = 0.26;
const DEMO_OUT = 520;
const DEMO_HOLD = 180;
const DEMO_RETURN = 620;

// Below this much travel there's nothing legible to demonstrate — a product
// with no colour or size options makes a stack that barely overflows.
const MIN_TRAVEL = 8;

// The grabber must clear the Add to Cart button's top edge by at least this
// much or it renders under glass and is invisible.
const MIN_CLEARANCE = 16;

/**
 * Samples a CSS cubic-bezier timing function.
 *
 * Needed because this animates scrollTop, which no CSS transition or the Web
 * Animations API can drive — so the curve has to be evaluated in JS. The
 * control points used below are the PDP's house curve (the same one behind
 * the CTA wipe, the label roll and the dot strip), so the demo moves like
 * everything else on the page. The theme has no `--ease-out` token; its
 * --ease-out-cubic is a different, flatter curve.
 *
 * @param {number} p1x
 * @param {number} p1y
 * @param {number} p2x
 * @param {number} p2y
 * @returns {(x: number) => number}
 */
function cubicBezier(p1x, p1y, p2x, p2y) {
  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;

  const sampleX = (t) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t) => ((ay * t + by) * t + cy) * t;

  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;

    // Binary subdivision rather than Newton-Raphson: no derivative to carry,
    // and over this many iterations the residual error is orders of magnitude
    // below one device pixel of scroll travel.
    let low = 0;
    let high = 1;
    let t = x;
    for (let i = 0; i < 20; i += 1) {
      const estimate = sampleX(t);
      if (Math.abs(estimate - x) < 1e-4) break;
      if (estimate > x) {
        high = t;
      } else {
        low = t;
      }
      t = (low + high) / 2;
    }
    return sampleY(t);
  };
}

const ease = cubicBezier(0.23, 1, 0.32, 1);

class ShabibiPdpLift {
  /**
   * @param {HTMLElement} section - The PDP section root ([data-shabibi-pdp]).
   */
  constructor(section) {
    this.infoCol = section.querySelector('.shabibi-pdp-col--info');
    this.infoCard = section.querySelector('.shabibi-info-card');
    this.productForm = section.querySelector('.shabibi-product-form');
    if (!this.infoCol || !this.infoCard || !this.productForm) return;

    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.frame = null;
    this.timer = null;
    this.lastWritten = null;
    this.listening = false;

    this.measure = this.measure.bind(this);
    this.cancel = this.cancel.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);

    this.measure();
    // Re-measured on load because the display webfont and the product images
    // can still change the card's height after this script first runs.
    window.addEventListener('load', this.measure);
    window.addEventListener('resize', this.measure);
    window.addEventListener('orientationchange', this.measure);
    this.mobileQuery.addEventListener('change', this.measure);

    // The ticket anchors the delay to the end of layer 2's entrance
    // animation. There isn't one — the stack renders in place — so the
    // anchor is `load`, which is strictly later than the rAF in
    // layoutForMode() that commits the panel's resting scroll position.
    if (document.readyState === 'complete') {
      this.scheduleDemo();
    } else {
      window.addEventListener('load', () => this.scheduleDemo(), { once: true });
    }
  }

  // ── Grabber clearance ──────────────────────────────────────────────
  //
  // Computed at rest rather than from the live rect: the card's top edge only
  // ever moves *up* from its resting position as the panel scrolls, so a live
  // reading on a scrolled panel would report a clearance the grabber doesn't
  // actually have when the stack settles back. Adding scrollTop back cancels
  // the scroll out.
  measure() {
    if (!this.mobileQuery.matches) {
      this.infoCol.removeAttribute('data-grabber');
      return;
    }

    const restingCardTop = this.infoCard.getBoundingClientRect().top + this.infoCol.scrollTop;
    const clearance = this.productForm.getBoundingClientRect().top - restingCardTop;

    if (clearance < MIN_CLEARANCE) {
      this.infoCol.setAttribute('data-grabber', 'suppressed');
    } else {
      this.infoCol.removeAttribute('data-grabber');
    }
  }

  // ── First-run demo ─────────────────────────────────────────────────

  scheduleDemo() {
    if (!this.canDemo()) return;

    // Listeners go on before the delay elapses, so input arriving during the
    // wait cancels the demo before it ever starts rather than being ignored
    // and then talked over.
    this.listen();

    this.timer = window.setTimeout(() => {
      this.timer = null;

      // Conditions can change across the delay — a tablet rotated into
      // landscape, or the stack scrolled and then returned to rest.
      if (!this.canDemo()) {
        this.finish();
        return;
      }

      if (document.visibilityState !== 'visible') {
        // Playing into a background tab spends the one shot on nobody. Wait
        // for the tab to come forward, and re-check everything then.
        document.addEventListener('visibilitychange', this.handleVisibility);
        return;
      }

      this.run();
    }, DEMO_DELAY);
  }

  handleVisibility() {
    if (document.visibilityState !== 'visible') return;
    document.removeEventListener('visibilitychange', this.handleVisibility);

    if (!this.canDemo()) {
      this.finish();
      return;
    }
    this.run();
  }

  canDemo() {
    if (!this.mobileQuery.matches) return false;
    // Reduced motion: the grabber alone carries the affordance. This is the
    // main reason both parts of the ticket ship together.
    if (this.reducedMotion.matches) return false;
    if (this.flagged()) return false;

    // Deep-linked or scroll-restored away from rest. The stack is already
    // showing what the demo would reveal, so demonstrating it is noise — and
    // lifting from a docked position would move the wrong way. Burn the flag
    // so it can't fire later in this session either.
    if (this.infoCol.scrollTop > 1) {
      this.flag();
      return false;
    }

    return true;
  }

  run() {
    // Read here rather than at schedule time: the webfont and the size chips
    // can still reflow the stack during the delay.
    const maxScroll = this.infoCol.scrollHeight - this.infoCol.clientHeight;
    const target = Math.round(maxScroll * DEMO_TRAVEL);

    if (target < MIN_TRAVEL) {
      this.finish();
      return;
    }

    this.animate(0, target, DEMO_OUT, () => {
      this.timer = window.setTimeout(() => {
        this.timer = null;
        // Return is slower than departure on purpose: a fast snap-back reads
        // as a rejection, a slow settle reads as a demonstration.
        this.animate(target, 0, DEMO_RETURN, () => this.finish());
      }, DEMO_HOLD);
    });
  }

  /**
   * @param {number} from
   * @param {number} to
   * @param {number} duration
   * @param {() => void} done
   */
  animate(from, to, duration, done) {
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      this.write(from + (to - from) * ease(progress));

      if (progress < 1) {
        this.frame = requestAnimationFrame(step);
        return;
      }
      this.frame = null;
      done();
    };

    this.frame = requestAnimationFrame(step);
  }

  // scrollTop rather than a transform on the panel: the panel is inset: 0, so
  // transforming it would slide a full-viewport box and open a visible gap at
  // its edge. Scrolling is also what the gesture being demonstrated actually
  // does, so the demo and the real thing move identically.
  write(value) {
    this.infoCol.scrollTop = value;
    // Read back rather than storing what was asked for. The browser may clamp
    // or round it, and handleScroll compares against the committed value.
    this.lastWritten = this.infoCol.scrollTop;
  }

  handleScroll() {
    // Every scrollTop write above fires this event, so a bare handler would
    // cancel the demo on its own first frame. Only a position this file did
    // not write is a real gesture.
    if (this.lastWritten !== null && Math.abs(this.infoCol.scrollTop - this.lastWritten) <= 1) {
      return;
    }
    this.cancel();
  }

  // Hand control over immediately. The animation is not finished, no snap-back
  // is played, and scrollTop is deliberately not written again — the stack
  // stays exactly where it is and the user's gesture takes over from there.
  // A demo that argues with a real gesture is worse than no demo.
  cancel() {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    this.finish();
  }

  finish() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.flag();
    this.unlisten();
  }

  // Bound at the document, not the panel: the panel is pointer-events: none
  // over its empty regions so swipes reach the image layer underneath, and a
  // swipe on the imagery is still user input that should stop the demo.
  listen() {
    if (this.listening) return;
    this.listening = true;

    document.addEventListener('pointerdown', this.cancel);
    // Passive, so cancelling can never delay the gesture it's yielding to.
    document.addEventListener('touchstart', this.cancel, { passive: true });
    document.addEventListener('wheel', this.cancel, { passive: true });
    document.addEventListener('keydown', this.cancel);
    // scroll doesn't bubble, so this one binds to the panel itself.
    this.infoCol.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  unlisten() {
    if (!this.listening) return;
    this.listening = false;

    document.removeEventListener('pointerdown', this.cancel);
    document.removeEventListener('touchstart', this.cancel);
    document.removeEventListener('wheel', this.cancel);
    document.removeEventListener('keydown', this.cancel);
    this.infoCol.removeEventListener('scroll', this.handleScroll);
  }

  // sessionStorage throws rather than no-oping in Safari private mode, and a
  // storage failure must not take the page down — worst case the demo plays
  // once more than intended.
  flagged() {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  flag() {
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (error) {
      /* no-op */
    }
  }
}

document.querySelectorAll('[data-shabibi-pdp]').forEach((section) => {
  new ShabibiPdpLift(section);
});
})();
